import React, { useState, useEffect } from 'react';
import { apiEndpoints, handleApiError } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  DollarSign,
  Calendar,
  FileText,
  CreditCard,
  RefreshCw,
  Eye,
  Download,
  Building,
  ShoppingCart,
  Wrench,
  Car,
  Lightbulb,
  Shield,
  X
} from 'lucide-react';

const OtherPayments = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filter state
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  
    const [refreshKey, setRefreshKey] = useState(0);
const [paymentData, setPaymentData] = useState({
    description: '',
    amount: '',
    type: 'Equipment Purchase',
    paymentDate: new Date().toISOString().split('T')[0],
    status: 'Completed',
    notes: '',
    recipient: '',
    category: 'Equipment',
    paymentMethod: 'Bank Transfer',
    referenceNumber: ''
  });

  const paymentTypes = [
    'Equipment Purchase',
    'Office Supplies',
    'Maintenance',
    'Utilities',
    'Insurance',
    'Transportation',
    'Marketing',
    'Training',
    'Legal Fees',
    'Rent',
    'Other'
  ];

  const categories = [
    'Equipment',
    'Operations',
    'Administrative',
    'Marketing',
    'Maintenance',
    'Utilities',
    'Other'
  ];

  const statusOptions = [
    'Completed',
    'Pending',
    'Cancelled',
    'Refunded'
  ];

  useEffect(() => {
    fetchPayments();
  }, []);

  // Handle search and filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPayments(1);
    }, 500); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, typeFilter, categoryFilter, statusFilter, dateFromFilter, dateToFilter]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'type':
        setTypeFilter(value);
        break;
      case 'category':
        setCategoryFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      case 'dateFrom':
        setDateFromFilter(value);
        break;
      case 'dateTo':
        setDateToFilter(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setCategoryFilter('');
    setStatusFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPayments(page);
  };

  const fetchPayments = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        type: typeFilter || undefined,
        category: categoryFilter || undefined
      };
      
      const response = await apiEndpoints.institutionalPayments.getPaginated(params);
      const data = response.data;
      
      if (data.payments) {
        setPayments(data.payments);
        setTotalPages(data.totalPages || 1);
        setTotalItems(data.totalItems || 0);
        setCurrentPage(data.currentPage || 1);
      } else {
        // Fallback to non-paginated response
        const response = await apiEndpoints.institutionalPayments.getAll();
        setPayments(response.data || []);
        setTotalPages(1);
        setTotalItems(response.data?.length || 0);
        setCurrentPage(1);
      }
    } catch (error) {
      handleApiError(error, 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map frontend field names to API field names
      const apiData = {
        paymentType: paymentData.type,
        description: paymentData.description,
        amount: parseFloat(paymentData.amount),
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
        recipient: paymentData.recipient,
        category: paymentData.category,
        notes: paymentData.notes,
        status: paymentData.status === 'Completed' ? 0 : paymentData.status === 'Pending' ? 1 : paymentData.status === 'Cancelled' ? 2 : 3,
        paymentMethod: paymentData.paymentMethod,
        referenceNumber: paymentData.referenceNumber
      };

      if (editingPayment) {
        // Update existing payment
        await apiEndpoints.institutionalPayments.update(editingPayment.id, apiData);
        toast.success('Payment updated successfully');
      } else {
        // Create new payment
        await apiEndpoints.institutionalPayments.create(apiData);
        toast.success('Payment created successfully');
      }
      
      setShowPaymentModal(false);
      setEditingPayment(null);
      setPaymentData({
        description: '',
        amount: '',
        type: 'Equipment Purchase',
        paymentDate: new Date().toISOString().split('T')[0],
        status: 'Completed',
        notes: '',
        recipient: '',
        category: 'Equipment',
        paymentMethod: 'Bank Transfer',
        referenceNumber: ''
      });
      fetchPayments();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      handleApiError(error, 'Failed to save payment');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    
    // Map API field names to frontend field names
    const getStatusText = (status) => {
      switch (status) {
        case 0: return 'Completed';
        case 1: return 'Pending';
        case 2: return 'Cancelled';
        case 3: return 'Refunded';
        default: return 'Completed';
      }
    };

    setPaymentData({
      description: payment.description || '',
      amount: payment.amount || '',
      type: payment.paymentType || 'Equipment Purchase',
      paymentDate: payment.paymentDate ? payment.paymentDate.split('T')[0] : new Date().toISOString().split('T')[0],
      status: getStatusText(payment.status),
      notes: payment.notes || '',
      recipient: payment.recipient || '',
      category: payment.category || 'Equipment',
      paymentMethod: payment.paymentMethod || 'Bank Transfer',
      referenceNumber: payment.referenceNumber || ''
    });
    setShowPaymentModal(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      await apiEndpoints.institutionalPayments.delete(paymentId);
      toast.success('Payment deleted successfully');
      fetchPayments();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      handleApiError(error, 'Failed to delete payment');
    }
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setEditingPayment(null);
    setPaymentData({
      description: '',
      amount: '',
      type: 'Equipment Purchase',
      paymentDate: new Date().toISOString().split('T')[0],
      status: 'Completed',
      notes: '',
      recipient: '',
      category: 'Equipment',
      paymentMethod: 'Bank Transfer',
      referenceNumber: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Refunded': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return t('institutional.otherPayments.statuses.completed');
      case 1:
        return t('institutional.otherPayments.statuses.pending');
      case 2:
        return t('institutional.otherPayments.statuses.cancelled');
      case 3:
        return t('institutional.otherPayments.statuses.refunded');
      default:
        return t('institutional.otherPayments.statuses.completed');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Equipment Purchase': return '🛠️';
      case 'Office Supplies': return '📦';
      case 'Maintenance': return '🔧';
      case 'Utilities': return '⚡';
      case 'Insurance': return '🛡️';
      case 'Transportation': return '🚗';
      case 'Marketing': return '📢';
      case 'Training': return '🎓';
      case 'Legal Fees': return '⚖️';
      case 'Rent': return '🏢';
      default: return '💼';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Equipment': return 'bg-purple-100 text-purple-800';
      case 'Operations': return 'bg-green-100 text-green-800';
      case 'Administrative': return 'bg-orange-100 text-orange-800';
      case 'Marketing': return 'bg-pink-100 text-pink-800';
      case 'Maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'Utilities': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalAmount = payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  const completedAmount = payments
    .filter(p => p.status === 'Completed')
    .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);

  // Calculate amounts by category
  const categoryTotals = categories.map(category => {
    const categoryPayments = payments.filter(p => p.category === category);
    const total = categoryPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    return { category, total, count: categoryPayments.length };
  }).filter(item => item.count > 0);

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 px-4 sm:px-6 lg:px-8 py-6" key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('institutional.otherPayments.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('institutional.otherPayments.subtitle')}</p>
            </div>
            <button
              onClick={() => {
                setEditingPayment(null);
                setPaymentData({
                  description: '',
                  amount: '',
                  type: 'Equipment Purchase',
                  paymentDate: new Date().toISOString().split('T')[0],
                  status: 'Completed',
                  notes: '',
                  recipient: '',
                  category: 'Equipment',
                  paymentMethod: 'Bank Transfer',
                  referenceNumber: ''
                });
                setShowPaymentModal(true);
              }}
              className="bg-blue-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm sm:text-base"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Payment
            </button>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('institutional.otherPayments.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.clear')}
            </button>
            
            <button
              onClick={() => fetchPayments(1)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="mt-6">
        {/* Payments List */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mt-4">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('institutional.otherPayments.paymentRecords')}</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading payments...</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center">
            <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || typeFilter || categoryFilter || statusFilter ? 'Try adjusting your search terms or filters' : 'Start by adding your first payment'}
            </p>
            {!searchTerm && !typeFilter && !categoryFilter && !statusFilter && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Add First Payment
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.typeDescription')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.recipient')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.category')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('institutional.otherPayments.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <span className="text-lg">{getTypeIcon(payment.type)}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.type}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.description}
                          </div>
                          {payment.notes && (
                            <div className="text-xs text-gray-400 mt-1">
                              {payment.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payment.recipient || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {payment.amount} DA
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(payment.category)}`}>
                        {payment.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                        {getStatusText(payment.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(payment)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {/* Pagination - Sticky at bottom */}
      {!loading && payments.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              {t('common.showing')} <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> {t('common.to')} <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> {t('common.of')} <span className="font-bold text-blue-600">{totalItems}</span> {t('common.results')}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.previous')}
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const startPage = Math.max(1, currentPage - 2);
                  const pageNum = startPage + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${
                        pageNum === currentPage
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingPayment ? t('common.editPayment') : t('common.addPayment')}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.paymentTypeLabel')} *</label>
                    <select
                      value={paymentData.type}
                      onChange={(e) => setPaymentData({...paymentData, type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      {paymentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.category')} *</label>
                    <select
                      value={paymentData.category}
                      onChange={(e) => setPaymentData({...paymentData, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.status')} *</label>
                    <select
                      value={paymentData.status}
                      onChange={(e) => setPaymentData({...paymentData, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.descriptionLabel')} *</label>
                  <input
                    type="text"
                    value={paymentData.description}
                    onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('payments.enterPaymentDescription')}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.recipient')}</label>
                    <input
                      type="text"
                      value={paymentData.recipient}
                      onChange={(e) => setPaymentData({...paymentData, recipient: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('payments.enterRecipientName')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.amountLabel')} *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.paymentDateLabel')} *</label>
                    <input
                      type="date"
                      value={paymentData.paymentDate}
                      onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <select
                      value={paymentData.paymentMethod}
                      onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Bank Transfer">{t('payments.bankTransfer')}</option>
                      <option value="Cash">{t('payments.cash')}</option>
                      <option value="Check">{t('payments.check')}</option>
                      <option value="Credit Card">{t('payments.creditCard')}</option>
                      <option value="Other">{t('payments.other')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
                    <input
                      type="text"
                      value={paymentData.referenceNumber}
                      onChange={(e) => setPaymentData({...paymentData, referenceNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('payments.enterReferenceNumber')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={paymentData.notes}
                      onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('payments.additionalNotes')}
                      rows="2"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {t('common.cancelLabel')}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingPayment ? t('common.updatePayment') : t('common.createPayment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OtherPayments;




















